import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Stack,
  TextField,
} from "@mui/material";
import {
  confirmShippingRouteDelivery,
  editShippingRoute,
  getShippingRouteOrderReturnPaymentPending,
  sendIfoodDeliveryConfirmationCode,
} from "../../api/shipping";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import ShippingRoutesList from "../shipping/ShippingRoutesList";
import LoadingBox from "../indicators/LoadingBox";
import {
  fetchShippingRoutes,
  setRouteIdToAdd,
  setShowOrdersListModal,
  setShowShippingRouteShowModal,
} from "../../store/actions/shippingRoutesSlice";
import { useDispatch, useSelector } from "react-redux";
import FormControl from "@mui/material/FormControl";
import {
  AutocompleteStyledPaper,
  AutocompleteStyledPopper,
  StyledAutocomplete,
} from "../styled/inputs/autocomplete";
import CustomConfirmationDialog from "../dialogs/CustomConfirmationDialog";
import { getDrivers } from "../../api/driver";
import Typography from "@mui/material/Typography";

const DashboardShippingRoutes = () => {
  const dispatch = useDispatch();

  const [openDriverDialog, setOpenDriverDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [error, setError] = useState(null);

  const loading = useSelector((state) => state.shippingRoutes.loading);
  const shippingRoutes = useSelector(
    (state) => state.shippingRoutes.shippingRoutes
  );

  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [options, setOptions] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const [ifoodConfirmationDialogOpen, setIfoodConfirmationDialogOpen] =
    useState(false);
  const [ifoodConfirmationCodes, setIfoodConfirmationCodes] = useState({});
  const [ifoodCodeErrors, setIfoodCodeErrors] = useState({});
  const [skippedOrders, setSkippedOrders] = useState({});
  const [
    selectedEquipmentForConfirmation,
    setSelectedEquipmentForConfirmation,
  ] = useState(null);
  const [ordersRequiringConfirmation, setOrdersRequiringConfirmation] =
    useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadDrivers();
  }, [isAutocompleteOpen]);

  const loadData = async () => {
    dispatch(fetchShippingRoutes({}));
  };

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      if (!isAutocompleteOpen) return;

      const { data } = await getDrivers();
      data.sort((a, b) => a.name.localeCompare(b.name));

      const newOptions = data.map((driver) => ({
        value: driver.driver_id,
        label: `${driver.easy_id} - ${driver.name}`,
        firstLetter: /[0-9]/.test(driver.name[0]) ? "0-9" : driver.name[0],
      }));

      setOptions(newOptions);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleShowShippingRouteShowModal = (shippingRouteId) => {
    dispatch(
      setShowShippingRouteShowModal({
        show: true,
        id: shippingRouteId,
      })
    );
  };

  const getOrdersRequiringiFoodConfirmation = (routeId) => {
    const route = shippingRoutes.find((r) => r.id === routeId);
    if (!route || !route.shipping_route_orders) return [];

    return route.shipping_route_orders
      .filter((order) => order.requires_ifood_confirmation_code)
      .map((order) => ({
        channel_order_id: order.fk_order.channel_order_id,
        order_index: order.fk_order.order_index,
      }));
  };

  const handleConfirmShippingRouteDelivery = async (id, equipment = null) => {
    try {
      const ordersRequiringConfirmation =
        getOrdersRequiringiFoodConfirmation(id);

      if (ordersRequiringConfirmation.length > 0) {
        setSelectedRouteId(id);
        setSelectedEquipmentForConfirmation(equipment);
        setOrdersRequiringConfirmation(ordersRequiringConfirmation);

        const initialCodes = {};
        ordersRequiringConfirmation.forEach((order) => {
          initialCodes[order.channel_order_id] = "";
        });
        setIfoodConfirmationCodes(initialCodes);
        setIfoodCodeErrors({});
        setSkippedOrders({});

        setIfoodConfirmationDialogOpen(true);
        return;
      }

      await proceedWithPaymentConfirmation(id, equipment);
    } catch (e) {
      console.error(e);
    }
  };

  const proceedWithPaymentConfirmation = async (id, equipment = null) => {
    try {
      const {
        data: { has_change, total_change_value, has_card },
      } = await getShippingRouteOrderReturnPaymentPending(id);

      if (has_change || has_card) {
        let dialogMessage = "";

        if (has_change) {
          dialogMessage += `O valor de troco de R$ ${total_change_value.toFixed(
            2
          )} foi devolvido?\n`;
        }

        if (has_card) {
          dialogMessage += `A maquininha ${equipment?.name} foi devolvida?`;
        }

        setSelectedRouteId(id);
        setDialogContent(dialogMessage);
        setDialogOpen(true);
      } else {
        const params = { rid: id };
        await confirmShippingRouteDelivery(params);
        await loadData();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSkipOrder = (orderId) => {
    setSkippedOrders((prev) => ({
      ...prev,
      [orderId]: true,
    }));
    setIfoodCodeErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[orderId];
      return newErrors;
    });
  };

  const handleUnskipOrder = (orderId) => {
    setSkippedOrders((prev) => {
      const newSkipped = { ...prev };
      delete newSkipped[orderId];
      return newSkipped;
    });
  };

  const handleiFoodConfirmationSubmit = async () => {
    const errors = {};
    let hasErrors = false;

    const ordersNeedingCodes = ordersRequiringConfirmation.filter(
      (order) => !skippedOrders[order.channel_order_id]
    );

    ordersNeedingCodes.forEach((order) => {
      const code = ifoodConfirmationCodes[order.channel_order_id];
      const isCodeMissing = !code || !code.trim();

      if (isCodeMissing) {
        errors[order.channel_order_id] = "Código de confirmação é obrigatório";
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setIfoodCodeErrors(errors);
      return;
    }

    try {
      const confirmationCodes = ordersNeedingCodes.map((order) => ({
        channel_order_id: order.channel_order_id,
        code: ifoodConfirmationCodes[order.channel_order_id].trim(),
      }));

      const body = {
        route_id: selectedRouteId,
        confirmation_codes: confirmationCodes,
      };

      await sendIfoodDeliveryConfirmationCode(body);

      setIfoodConfirmationDialogOpen(false);
      setIfoodConfirmationCodes({});
      setIfoodCodeErrors({});
      setSkippedOrders({});
      setOrdersRequiringConfirmation([]);

      await proceedWithPaymentConfirmation(
        selectedRouteId,
        selectedEquipmentForConfirmation
      );
    } catch (error) {
      console.error("Error sending iFood confirmation codes:", error);
      dispatchSnackBarMessage(
        Date.now(),
        "Erro ao enviar códigos de confirmação iFood",
        "error"
      );
    }
  };

  const handleiFoodConfirmationClose = () => {
    setIfoodConfirmationDialogOpen(false);
    setIfoodConfirmationCodes({});
    setIfoodCodeErrors({});
    setSkippedOrders({});
    setOrdersRequiringConfirmation([]);
    setSelectedRouteId(null);
    setSelectedEquipmentForConfirmation(null);
  };

  const handleAddNewOrderToShippingRoute = async (shippingRouteId) => {
    try {
      await dispatch(setRouteIdToAdd(shippingRouteId));
      await dispatch(setShowOrdersListModal(true));
    } catch (e) {
      dispatchSnackBarMessage(
        Date.now(),
        "Erro ao iniciar edição de rota",
        "error"
      );
      console.error(e);
    }
  };

  const handleEditShippingRouteDriver = async (routeId) => {
    setOpenDriverDialog(true);
    setLoadingDrivers(true);

    setSelectedRouteId(routeId);

    try {
      const { data } = await loadDrivers();
      const options = data.map((driver) => ({
        label: driver.name,
        value: driver.id,
      }));
      setOptions(options);
    } catch (e) {
      console.error("Failed to fetch drivers", e);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleDriverDialogClose = () => {
    setOpenDriverDialog(false);
    setSelectedDriver(null);
    setError(null);
  };

  const handleDriverDialogSubmit = async () => {
    if (!selectedDriver) {
      setError("Selecione um motorista");
      return;
    }

    try {
      await editShippingRoute(selectedRouteId, selectedDriver.value);
      dispatchSnackBarMessage(
        Date.now(),
        "Entregador definido com sucesso",
        "success"
      );
      setOpenDriverDialog(false);
      await loadData();
    } catch (e) {
      dispatchSnackBarMessage(Date.now(), "Erro ao editar entregador", "error");
      console.error(e);
    }
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleDialogConfirm = async () => {
    try {
      const params = { rid: selectedRouteId };
      await confirmShippingRouteDelivery(params);
      await loadData();
      setDialogOpen(false);
    } catch (e) {
      dispatchSnackBarMessage(Date.now(), "Erro ao confirmar retorno", "error");
      console.error(e);
    }
  };

  if (loading) {
    return <LoadingBox height={30} />;
  }

  return (
    <>
      <Slide in={!loading} direction="up" mountOnEnter unmountOnExit>
        <Box sx={{ overflow: "auto", p: 1 }}>
          {!loading && (
            <ShippingRoutesList
              isDashboard
              shippingRoutes={shippingRoutes}
              onShowMore={() => loadData(false)}
              onViewRoute={handleShowShippingRouteShowModal}
              onEditDriver={handleEditShippingRouteDriver}
              onDelivery={handleConfirmShippingRouteDelivery}
              onAddNewOrder={handleAddNewOrderToShippingRoute}
              showMore={false}
            />
          )}
        </Box>
      </Slide>

      <Dialog
        open={ifoodConfirmationDialogOpen}
        onClose={handleiFoodConfirmationClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            Confirmação de Entrega
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            Esta rota contém {ordersRequiringConfirmation.length} pedido(s)
            iFood que requerem código de confirmação. Por favor, insira o código
            fornecido pelo cliente para cada pedido.
          </Typography>

          <Stack spacing={3}>
            {ordersRequiringConfirmation.map((order, index) => {
              const isOrderSkipped = skippedOrders[order.channel_order_id];

              return (
                <Box key={order.channel_order_id}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      autoFocus={index === 0}
                      fullWidth
                      label={`Código - Pedido #${order.order_index}`}
                      variant="outlined"
                      value={
                        ifoodConfirmationCodes[order.channel_order_id] || ""
                      }
                      onChange={(e) => {
                        setIfoodConfirmationCodes((prev) => ({
                          ...prev,
                          [order.channel_order_id]: e.target.value,
                        }));
                        setIfoodCodeErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[order.channel_order_id];
                          return newErrors;
                        });
                      }}
                      error={!!ifoodCodeErrors[order.channel_order_id]}
                      helperText={ifoodCodeErrors[order.channel_order_id]}
                      placeholder={`Digite o código para o pedido #${order.order_index}`}
                      disabled={isOrderSkipped}
                      inputProps={{
                        style: { textTransform: "uppercase" },
                        maxLength: 10,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />
                    <Button
                      variant={isOrderSkipped ? "outlined" : "text"}
                      color={isOrderSkipped ? "primary" : "secondary"}
                      onClick={() =>
                        isOrderSkipped
                          ? handleUnskipOrder(order.channel_order_id)
                          : handleSkipOrder(order.channel_order_id)
                      }
                      sx={{
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        px: 2,
                        height: "56px",
                      }}
                    >
                      {isOrderSkipped
                        ? "Adicionar código"
                        : "Não tenho o código"}
                    </Button>
                  </Stack>

                  {isOrderSkipped && (
                    <Box sx={{ mt: 1, ml: 0 }}>
                      <Chip
                        label="Código não fornecido"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontWeight: 500,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleiFoodConfirmationClose}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleiFoodConfirmationSubmit}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <CustomConfirmationDialog
        onClose={handleCloseDialog}
        dialogContent={dialogContent}
        title={"Confirmar Retorno"}
        open={dialogOpen}
        onConfirm={handleDialogConfirm}
      />

      <Dialog
        open={openDriverDialog}
        onClose={handleDriverDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            height: "80vh",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>Selecione o motorista</DialogTitle>
        <DialogContent
          sx={{
            height: "calc(100% - 64px - 52px)",
            overflowY: "auto",
          }}
        >
          <FormControl error={!!error} sx={{ mb: 2, width: "100%" }}>
            <StyledAutocomplete
              disablePortal={false}
              id="select-new-driver-to-deliver-order"
              open={isAutocompleteOpen}
              onOpen={() => {
                setIsAutocompleteOpen(true);
              }}
              onClose={() => {
                setIsAutocompleteOpen(false);
              }}
              loading={loadingDrivers}
              options={options}
              getOptionLabel={(option) => option.label}
              groupBy={(option) => option.firstLetter}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Motorista"
                  variant="outlined"
                  error={!!error}
                  helperText={error}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>{option.label}</Typography>
                </li>
              )}
              PopperComponent={(props) => (
                <AutocompleteStyledPopper {...props} />
              )}
              PaperComponent={(props) => <AutocompleteStyledPaper {...props} />}
              onChange={(event, value) => {
                setSelectedDriver(value);
                setError(null);
              }}
              sx={{ mb: 1 }}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDriverDialogClose} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleDriverDialogSubmit} color="primary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(DashboardShippingRoutes);
